import Template from "../../components/Template";
import React, { useState, useEffect, useRef } from "react";
import api from "../../services/api";
import AlertDialog from "../../components/AlertDialog";
import { formatCurrencyFromDb } from "../../utils/masks";
import usePermission from "../../hooks/usePermission";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from "xlsx";
import { useAuth } from '../../context/AuthContext';
import { formatDateWithoutHour } from "../../utils/date";
import useRequisicaoMaisRecente from '../../hooks/useRequisicaoMaisRecente';
import logoEmpresa from "../../components/logoEmpresaBase64"; // Importar o logo

const RelatorioNotasPorObra = () => {
    const { auth } = useAuth();
    const [lista, setLista] = useState([]);
    const [obras, setObras] = useState([]); // De 'clientes' para 'obras'
    const [fornecedores, setFornecedores] = useState([]);
    const [columns, setColumns] = useState([]);
    const [menuOpen, setMenuOpen] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [message, setMessage] = useState("");

    // Estado para armazenar os OBJETOS COMPLETOS das linhas selecionadas
    const [selectedRows, setSelectedRows] = useState([]);

    // Filtro principal agora é 'obra'
    const [filters, setFilters] = useState({
        obra: "",
        dataInicial: "",
        dataFinal: "",
        fornecedor: "",
        tipoNota: ""
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    const buttonRef = useRef(null);
    const buttonFiltrarRef = useRef(null);

    // Lógica para selecionar/deselecionar uma única linha
    const handleRowSelect = (row) => {
        setSelectedRows(prev => {
            const isSelected = prev.some(item => item.id === row.id);
            if (isSelected) {
                return prev.filter(item => item.id !== row.id);
            } else {
                return [...prev, row];
            }
        });
    };

    // Lógica para o checkbox "Selecionar Todos" do cabeçalho
    const handleSelectAllOnPage = (e) => {
        if (e.target.checked) {
            setSelectedRows(prev => {
                const pageIds = lista.map(item => item.id);
                const filtered = prev.filter(item => !pageIds.includes(item.id));
                return [...filtered, ...lista];
            });
        } else {
            const pageIds = lista.map(item => item.id);
            setSelectedRows(prev => prev.filter(item => !pageIds.includes(item.id)));
        }
    };

    // Definição das colunas e busca inicial de Obras
    useEffect(() => {
        setColumns([
            {
                id: 'select',
                label: (
                    <input
                        type="checkbox"
                        title="Selecionar todos nesta página"
                        // O estado "checked" do checkbox principal depende se todos os itens visíveis já estão selecionados
                        checked={lista.length > 0 && lista.every(item => selectedRows.some(sel => sel.id === item.id))}
                        onChange={handleSelectAllOnPage}
                    />
                ),
                visible: true,
                formatter: (value, row) => (
                    <input
                        type="checkbox"
                        checked={selectedRows.some(sel => sel.id === row.id)}
                        onChange={() => handleRowSelect(row)}
                    />
                )
            },
            { id: 'numero', label: 'Número NF', visible: true, formatter: (value) => value || "N/A" },
            { id: 'dataEmissao', label: 'Data Emissão', visible: true, formatter: (value) => (value ? formatDateWithoutHour(value) : "") },
            { id: 'valorTotal', label: 'Valor Total', visible: true, formatter: (value) => formatCurrencyFromDb(value) },
            { id: 'fornecedor', label: 'Fornecedor', visible: true, formatter: (value) => value ? `${value.nomeFornecedor?.toUpperCase() || value.razaoSocial?.toUpperCase()} - ${value.cpfCnpj}` : 'N/A' },
            { id: 'obra', label: 'Obra', visible: true, formatter: (value) => value?.nomeProjeto?.toUpperCase() || 'N/A' },
            { id: 'tipo', label: 'Tipo', visible: true, formatter: (value) => value },
        ]);

        const fetchInitialData = async () => {
            try {
                // Busca a lista de Obras e Fornecedores para popular os filtros
                const [obrasRes, fornecedoresRes] = await Promise.all([
                    api.get("/obras/listar/todas"),
                    api.get("/fornecedores/ativos/todos")
                ]);
                setObras(obrasRes.data);
                setFornecedores(fornecedoresRes.data);
            } catch (error) {
                console.error("Erro ao carregar dados iniciais:", error);
                setMessage("Erro ao carregar a lista de obras e fornecedores.");
                setShowSuccessDialog(true);
            }
        };
        fetchInitialData();
    }, []);

    // Redefine as colunas sempre que selectedRows ou lista mudarem para atualizar os checkboxes
    useEffect(() => {
        setColumns(prev => [...prev]); // Força re-render das colunas
    }, [lista, selectedRows]); // Depende de `selectedRows` para re-renderizar o header checkbox

    // Função de busca adaptada para o endpoint de Obras
    // Descarta respostas de filtros que já foram superados por outro mais novo:
    // sem isto, a resposta lenta de um filtro anterior sobrescrevia a tela.
    const requisicaoMaisRecente = useRequisicaoMaisRecente();

    const fetchNotasFiscaisPorObra = async (currentFilters) => {
        if (!currentFilters.obra) {
            setMessage("Por favor, selecione uma obra para filtrar.");
            setShowSuccessDialog(true);
            return;
        }

        try {
            const params = new URLSearchParams();
            if (currentFilters.dataInicial) params.append('dataInicial', currentFilters.dataInicial);
            if (currentFilters.dataFinal) params.append('dataFinal', currentFilters.dataFinal);
            if (currentFilters.fornecedor) params.append('fornecedor', currentFilters.fornecedor);
            if (currentFilters.tipoNota) params.append('tipoNota', currentFilters.tipoNota);

            // Consome o novo endpoint de obras
            const idRequisicao = requisicaoMaisRecente.iniciar();
            const response = await api.get(`/relatorios/obra/${currentFilters.obra}/notasfiscais?${params.toString()}`);
            // Chegou atrasada e ja foi superada por outro filtro: descarta.
            if (!requisicaoMaisRecente.ehAtual(idRequisicao)) return;
            setLista(response.data || []);
            setTotalRecords(response.data.length || 0);
            setCurrentPage(1);
        } catch (error) {
            console.error("Erro ao buscar notas fiscais:", error);
            setMessage(error.response?.data?.error || "Erro ao buscar notas fiscais.");
            setShowSuccessDialog(true);
            setLista([]);
            setTotalRecords(0);
        }
    };

    const applyFilters = () => {
        fetchNotasFiscaisPorObra(filters);
        setMenuOpen(null);
    };

    const clearFilters = () => {
        setFilters({ obra: "", dataInicial: "", dataFinal: "", fornecedor: "", tipoNota: "" });
        setLista([]);
        setTotalRecords(0);
        setCurrentPage(1);
        setSelectedRows([]); // Limpa seleções também
    };

    const exportarDados = async (exportFunction) => {
        const hasSelection = selectedRows.length > 0;

        if (!hasSelection && (!totalRecords || totalRecords === 0)) {
            setMessage('Não há dados para exportar!');
            setShowSuccessDialog(true);
            return;
        }

        let dataToExport = [];

        if (hasSelection) {
            dataToExport = selectedRows;
            exportFunction(dataToExport);
        } else {
            setMessage('Preparando todos os dados para exportação... Isso pode levar um momento.');
            setShowSuccessDialog(true);
            try {
                const params = new URLSearchParams();
                if (filters.dataInicial) params.append('dataInicial', filters.dataInicial);
                if (filters.dataFinal) params.append('dataFinal', filters.dataFinal);
                if (filters.fornecedor) params.append('fornecedor', filters.fornecedor);
                if (filters.tipoNota) params.append('tipoNota', filters.tipoNota);

                const response = await api.get(`/relatorios/obra/${filters.obra}/notasfiscais?${params.toString()}`);
                dataToExport = response.data || [];
                exportFunction(dataToExport);
            } catch (error) {
                console.error("Erro ao exportar dados:", error);
                setMessage("Falha ao preparar dados para exportação.");
                setShowSuccessDialog(true);
            }
        }
    };

    const handlePageChange = (page) => setCurrentPage(page);

    const toggleColumnVisibility = (id) => setColumns((prev) => prev.map((col) => col.id === id ? { ...col, visible: !col.visible } : col));

    // Lógica de paginação
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startRecord = totalRecords > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const endRecord = Math.min(currentPage * pageSize, totalRecords);
    const paginatedLista = lista.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    const pageWindowSize = 5;
    const startPage = Math.max(1, currentPage - Math.floor(pageWindowSize / 2));
    const endPage = Math.min(totalPages, startPage + pageWindowSize - 1);
    const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

    // Funções de exportação adaptadas para Obra
    const exportarNotasFiscaisPDF = (data) => {
        if (!data || data.length === 0) {
            setMessage('Não há dados para exportar!');
            setShowSuccessDialog(true);
            return;
        }

        const doc = new jsPDF({ orientation: "landscape" });
        const selectedObra = obras.find(o => o.id === parseInt(filters.obra));

        doc.setFontSize(16);
        doc.text("Relatório de Notas Fiscais por Obra", 14, 20);
        doc.setFontSize(10);
        doc.text(`Obra: ${selectedObra?.nomeProjeto.toUpperCase() || 'Não especificada'}`, 14, 28);
        if (selectedRows.length > 0) {
            doc.text(`Exportando ${selectedRows.length} linha(s) selecionada(s).`, 14, 34);
        }

        const visibleColumns = columns.filter(c => c.visible && c.id !== 'select');
        const head = [visibleColumns.map(c => c.label)];
        const body = data.map(row => visibleColumns.map(col => col.formatter ? col.formatter(row[col.id], row) : (row[col.id] || '')));

        // Calcular total da coluna valorTotal
        const totalValor = data.reduce((sum, item) => sum + (Number(item.valorTotal) || 0), 0);
        const totalFormatado = formatCurrencyFromDb(totalValor);

        // Adicionar linha de total
        const totalRow = Array(head[0].length).fill('');
        const valorTotalIndex = visibleColumns.findIndex(c => c.id === 'valorTotal');
        if (valorTotalIndex !== -1) {
            totalRow[valorTotalIndex - 1] = { content: 'TOTAL:', styles: { fontStyle: 'bold', halign: 'right' } };
            totalRow[valorTotalIndex] = { content: totalFormatado, styles: { fontStyle: 'bold' } };
        }
        body.push(totalRow);

        autoTable(doc, { 
            startY: selectedRows.length > 0 ? 40 : 35, 
            head, 
            body, 
            headStyles: { fillColor: [30, 120, 180] },
            bodyStyles: { valign: 'middle' },
            didDrawCell: (data) => {
                if (data.row.index === body.length - 1) { 
                    doc.setFont(undefined, 'bold'); 
                }
            }
        });

        // Adicionar informações do rodapé
        const pageCount = doc.internal.getNumberOfPages();
        const dataEmissao = new Date().toLocaleString('pt-BR');
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(100);
            doc.text(`Emitido por: ${auth.user.name || "Sistema"}`, 14, doc.internal.pageSize.height - 15);
            doc.text(`Data/Hora: ${dataEmissao}`, 14, doc.internal.pageSize.height - 10);
            doc.text(`Página ${i} de ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10, { align: 'right' });
            if (logoEmpresa) {
                const logoWidth = 40, logoHeight = 12, pageWidth = doc.internal.pageSize.getWidth();
                doc.addImage(logoEmpresa, 'PNG', (pageWidth - logoWidth) / 2, doc.internal.pageSize.height - 18, logoWidth, logoHeight);
            }
        }

        doc.save("relatorio_notas_fiscais_por_obra.pdf");
    };

    const exportarNotasFiscaisExcel = (data) => {
        if (!data || data.length === 0) {
            setMessage('Não há dados para exportar!');
            setShowSuccessDialog(true);
            return;
        }

        const selectedObra = obras.find(o => o.id === parseInt(filters.obra));
        const nomeObra = selectedObra ? selectedObra.nomeProjeto.toUpperCase() : 'OBRA_NAO_ESPECIFICADA';
        const reportHeaders = [["Relatório de Notas Fiscais por Obra"], [`Obra: ${nomeObra}`], []];
        if (selectedRows.length > 0) {
            reportHeaders.splice(2, 0, [`Exportando ${selectedRows.length} linha(s) selecionada(s).`]);
        }
        
        const visibleColumns = columns.filter(c => c.visible && c.id !== 'select');
        const tableHeaders = visibleColumns.map(c => c.label);
        const tableRows = data.map(row => visibleColumns.map(col => {
            const value = row[col.id];
            if (col.id === 'valorTotal') return typeof value === 'number' ? value : parseFloat(String(value || '0').replace(',', '.'));
            return col.formatter ? col.formatter(value, row) : (value || '');
        }));

        // Calcular total da coluna valorTotal
        const totalValor = data.reduce((sum, item) => sum + (Number(item.valorTotal) || 0), 0);
        
        // Criar linha de total
        const totalRow = Array(tableHeaders.length).fill('');
        const valorTotalIndex = visibleColumns.findIndex(c => c.id === 'valorTotal');
        if (valorTotalIndex !== -1) {
            totalRow[valorTotalIndex - 1] = "TOTAL:";
            totalRow[valorTotalIndex] = totalValor;
        }

        const finalData = [...reportHeaders, tableHeaders, ...tableRows, [], totalRow];
        const worksheet = XLSX.utils.aoa_to_sheet(finalData);
        
        // Formatação da coluna de valor
        if (valorTotalIndex > -1) {
            const valorColLetter = XLSX.utils.encode_col(valorTotalIndex);
            const startRow = reportHeaders.length + 2; // Ajuste dinâmico baseado no cabeçalho
            for (let i = startRow; i <= tableRows.length + (startRow - 1); i++) {
                const cellRef = `${valorColLetter}${i}`;
                if (worksheet[cellRef]) {
                    worksheet[cellRef].t = 'n';
                    worksheet[cellRef].z = 'R\$ #,##0.00';
                }
            }
            // Formatação da célula de total
            const totalCellRef = `${valorColLetter}${tableRows.length + startRow + 1}`;
            if (worksheet[totalCellRef]) {
                worksheet[totalCellRef].t = 'n';
                worksheet[totalCellRef].z = 'R\$ #,##0.00';
            }
        }
        
        worksheet['!cols'] = tableHeaders.map(header => ({ wch: header.length + 10 }));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Notas Fiscais por Obra");
        XLSX.writeFile(workbook, `Relatorio_NF_${nomeObra.replace(/\s+/g, '_')}.xlsx`);
    };

    const handleClickOutside = (event) => {
        if (buttonRef.current && !buttonRef.current.contains(event.target) && buttonFiltrarRef.current && !buttonFiltrarRef.current.contains(event.target)) {
            setMenuOpen(null);
        }
    };
    useEffect(() => { document.addEventListener("mousedown", handleClickOutside); return () => document.removeEventListener("mousedown", handleClickOutside); }, []);

    return (
        <Template>
            <div className="h-auto w-full p-4 md:p-8 flex flex-col gap-6">
                <div className="text-stone-800 text-xl md:text-2xl font-medium">Relatório de Notas Fiscais por Obra</div>

                <div className="flex flex-wrap gap-2 md:gap-4 items-center">
                    <div className="flex-grow min-w-0 flex items-center gap-2">
                        <div className="text-stone-600 text-sm font-medium">Ver</div>
                        <select className="w-20 h-8 pl-2 pr-2 py-1 bg-white rounded-md border border-slate-300 text-sm" value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setCurrentPage(1); }}>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                            <option value={500}>500</option>
                            <option value={1000}>1000</option>
                        </select>
                        <div className="text-stone-600 text-sm font-medium">Resultados</div>
                    </div>

                    <div className="flex flex-wrap gap-2 flex-none w-full md:w-auto">
                        <div ref={buttonRef} className="relative justify-end items-center gap-1 flex cursor-pointer" onClick={() => setMenuOpen(menuOpen === "colunas" ? null : "colunas")}>
                            <div className="justify-start items-center gap-2 flex"><img className="w-6 h-6" src="/images/icon/columns.png" alt="Colunas" /><div className="text-stone-600 text-sm font-medium">Colunas</div></div>
                            <img className="w-6 h-4 px-1" src="/images/icon/chevron-down.png" alt="Seta" />
                            {menuOpen === "colunas" && (<div className="absolute top-full right-0 mt-2 w-48 bg-white border rounded-md shadow-lg z-50 p-4 flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>{columns.map((col) => (<div key={col.id} className="flex items-center gap-2"><input type="checkbox" id={col.id} checked={col.visible} disabled={col.id === 'select'} onChange={() => toggleColumnVisibility(col.id)} /><label htmlFor={col.id} className="text-sm text-gray-700">{col.label}</label></div>))}</div>)}
                        </div>
                        <div ref={buttonFiltrarRef} className="relative justify-start items-center gap-1 flex cursor-pointer" onClick={() => setMenuOpen(menuOpen === "filtro" ? null : "filtro")}>
                            <div className="justify-start items-center gap-2 flex"><img className="w-8 h-8" src="/images/icon/filter.png" alt="Filtrar" /><div className="text-stone-600 text-sm font-medium">Filtrar</div></div>
                            <img className="w-6 h-4 px-1" src="/images/icon/chevron-down.png" alt="Seta" />
                            {menuOpen === "filtro" && (
                                <div className="absolute top-full right-0 mt-2 w-[350px] bg-white rounded-md shadow-lg z-50 p-4 border" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex flex-col gap-4">
                                        <div className="text-stone-800 text-xl font-medium">Filtrar por Obra</div>
                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-slate-700 text-sm font-medium">Obra (Obrigatório)</label>
                                            <select name="obra" className="w-full px-3 py-2 bg-white rounded-md border border-slate-300" value={filters.obra} onChange={(e) => setFilters({ ...filters, obra: e.target.value })}>
                                                <option value="">Selecione uma obra</option>
                                                {obras.map((o) => (<option key={o.id} value={o.id}>{o.nomeProjeto.toUpperCase()}</option>))}
                                            </select>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="flex flex-col gap-1.5"><label className="text-slate-700 text-sm font-medium">Data Inicial</label><input type="date" name="dataInicial" className="w-full px-3 py-2 bg-white rounded-md border" value={filters.dataInicial} onChange={(e) => setFilters({ ...filters, dataInicial: e.target.value })} /></div>
                                            <div className="flex flex-col gap-1.5"><label className="text-slate-700 text-sm font-medium">Data Final</label><input type="date" name="dataFinal" className="w-full px-3 py-2 bg-white rounded-md border" value={filters.dataFinal} onChange={(e) => setFilters({ ...filters, dataFinal: e.target.value })} /></div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="flex flex-col gap-1.5">
                                                <label className="text-slate-700 text-sm font-medium">Fornecedor</label>
                                                <select
                                                    name="fornecedor"
                                                    className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700"
                                                    value={filters.fornecedor}
                                                    onChange={(e) => setFilters({ ...filters, fornecedor: e.target.value })}
                                                >
                                                    <option value="">Todos os fornecedores</option>
                                                    {fornecedores.map((f) => (
                                                        <option key={f.id} value={f.id}>
                                                            {(f.nomeFornecedor || f.razaoSocial)?.toUpperCase()}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="flex flex-col gap-1.5">
                                                <label className="text-slate-700 text-sm font-medium">Tipo de Nota</label>
                                                <select
                                                    name="tipoNota"
                                                    className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700"
                                                    value={filters.tipoNota}
                                                    onChange={(e) => setFilters({ ...filters, tipoNota: e.target.value })}
                                                >
                                                    <option value="">Todos os tipos</option>
                                                    <option value="Material">Material</option>
                                                    <option value="Serviço">Serviço</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between gap-4 mt-4">
                                            <button className="px-4 py-2 w-full bg-white rounded-md border border-stone-500 flex items-center justify-center gap-2" onClick={clearFilters}><img className="w-4 h-4" src="/images/icon/eraser.png" alt="Limpar" /><div className="text-stone-500 text-sm">Limpar</div></button>
                                            <button className="px-4 py-2 w-full bg-stone-600 rounded-md flex items-center justify-center gap-2" onClick={applyFilters}><img className="w-4 h-4" src="/images/icon/filter-white.png" alt="Filtrar" /><div className="text-white text-sm">Filtrar</div></button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-4">
                            {usePermission('EXPORTAR_RELATORIOS_NOTAS_OBRA') && (<>
                                <button type='button' className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md flex items-center gap-2" onClick={() => exportarDados(exportarNotasFiscaisExcel)}>
                                    <img className="w-4 h-4" src="/images/icon/upload.png" />
                                    <div className="text-white text-sm font-medium font-['Inter'] leading-normal">Excel</div></button>
                                <button type='button' className="flex px-4 py-2 bg-slate-600 rounded-md border border-stone-600 justify-center items-center gap-2 cursor-pointer" onClick={() => exportarDados(exportarNotasFiscaisPDF)}>
                                    <img className="w-4 h-4" src="/images/icon/pdf.png" alt="PDF" />
                                    <div className="text-white text-sm font-medium font-['Inter'] leading-normal">PDF</div></button>
                            </>)}
                        </div>
                    </div>
                </div>

                <div className="flex justify-between items-center px-1">
                    <div className="text-sm text-gray-600">
                        {selectedRows.length > 0 && `${selectedRows.length} linha(s) selecionada(s).`}
                    </div>
                    {selectedRows.length > 0 && (
                        <button onClick={() => setSelectedRows([])} className="text-sm text-blue-600 hover:text-blue-800 font-semibold">
                            Limpar seleção
                        </button>
                    )}
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border-collapse border border-gray-200">
                        <thead className="bg-stone-200">
                            <tr>{columns.filter(col => col.visible).map((col) => (<th key={col.id} className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold">{col.label}</th>))}</tr>
                        </thead>
                        <tbody>
                            {paginatedLista.length > 0 ? (
                                paginatedLista.map((doc, index) => (
                                    <tr key={doc.id || index} className="hover:bg-gray-50">
                                        {columns.filter(col => col.visible).map(col => (<td key={`${col.id}-${doc.id}`} className="px-4 py-2 border border-slate-200 text-slate-700 text-base">{col.formatter(doc[col.id], doc)}</td>))}
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan={columns.filter(c => c.visible).length} className="text-center py-10 text-slate-500">Nenhum registro encontrado. Utilize o filtro para iniciar a busca.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalRecords > 0 && (
                    <div className="self-stretch justify-end items-center gap-2.5 inline-flex">
                        <div className="grow shrink basis-0 text-slate-500 text-base font-normal">Mostrando de {startRecord} até {endRecord} de {totalRecords} registros.</div>
                        <div className="justify-start items-start gap-4 flex">
                            <button className={`w-10 h-10 rounded border border-gray-300 disabled:opacity-50 flex justify-center items-center`} disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}><img className="w-6 h-6" src="/images/icon/keyboard-arrow-left.png" /></button>
                            {pageNumbers.map((page) => (<button key={page} className={`w-10 h-10 rounded flex justify-center items-center font-semibold ${page === currentPage ? "bg-stone-600 text-white" : "bg-gray-200 text-gray-600"}`} onClick={() => handlePageChange(page)}>{page}</button>))}
                            <button className={`w-10 h-10 rounded border border-gray-300 disabled:opacity-50 flex justify-center items-center`} disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}><img className="w-6 h-6" src="/images/icon/keyboard-arrow-right.png" /></button>
                        </div>
                    </div>
                )}
                <AlertDialog isVisible={showSuccessDialog} message={message} onClose={() => setShowSuccessDialog(false)} />
            </div>
        </Template>
    );
};

export default RelatorioNotasPorObra;